// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";
import type { TaskCardSettings } from "@/features/tasks/types";

export type TaskDefinition = components["schemas"]["TasksResponse"];
export type TaskDefinitionDetail = components["schemas"]["TasksDetailsResponse"];
export type TaskScope = components["schemas"]["TasksScopeResponse"];
export type TaskScopeKind = components["schemas"]["TasksScopeEnum"];
export type TaskAvailabilityEntry = components["schemas"]["TasksAvailabilityEntryResponse"];
export type TasksSortBy = components["schemas"]["TasksSortByEnum"];
export type TaskKind = components["schemas"]["TasksKindEnum"];
export type BuiltInTaskType = components["schemas"]["BuiltInTaskTypeSummary"];

export interface TasksResult {
  tasks: TaskDefinition[];
  count: number;
}

/**
 * The card as the editor consumes it. The generated types widen every free-form JSON column to
 * `Record<string, never>[]`, so the settings blocks are re-typed with the Action Tool's real
 * shapes from `@/features/tasks/types` at this boundary and nowhere else.
 */
export interface TaskCard extends Omit<components["schemas"]["TasksCardResponse"], "settings" | "baseSettings"> {
  settings: TaskCardSettings;
  baseSettings: TaskCardSettings;
}

/** A built-in type carries no `settings` — it has no definition to configure. */
export interface TaskDefinitionDetailResult extends Omit<TaskDefinitionDetail, "settings"> {
  settings?: TaskCardSettings;
}

interface TasksParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: TasksSortBy;
  orderBy?: "asc" | "desc";
  onlyActive?: boolean;
  kind?: TaskKind;
}

/** A chain/store scope, or `{}` for the global card. */
export interface TaskCardScope {
  chainName?: string;
  storeName?: string;
}

export function useTasksQuery(params: TasksParams = {}) {
  const { page = 1, limit = 20, search, sortBy, orderBy, onlyActive, kind } = params;

  return useQuery({
    queryKey: ["tasks", "list", { page, limit, search, sortBy, orderBy, onlyActive, kind }],
    queryFn: async () => {
      const res = await api.GET("/v1/tasks/", {
        params: {
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[onlyActive]": onlyActive,
            "filter[kind]": kind,
          },
        },
      });
      return res.data as unknown as TasksResult;
    },
  });
}

export function useTaskQuery(subtype: string) {
  return useQuery({
    queryKey: ["tasks", "detail", subtype],
    queryFn: async () => {
      const res = await api.GET("/v1/tasks/{subtype}", {
        params: { path: { subtype } },
      });
      if (res.error) throw new Error("Failed to load custom task definition");
      return res.data as unknown as TaskDefinitionDetailResult;
    },
    enabled: !!subtype,
  });
}

export type TaskAvailabilitySortBy = components["schemas"]["TasksAvailabilitySortByEnum"];

interface TaskAvailabilityParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: TaskAvailabilitySortBy;
  orderBy?: "asc" | "desc";
  scope?: TaskScopeKind;
  chainName?: string;
  storeName?: string;
  isActive?: boolean;
  isDefault?: boolean;
}

export function useTaskAvailabilityQuery(subtype: string, params: TaskAvailabilityParams = {}, enabled = true) {
  const { page = 1, limit = 20, search, sortBy, orderBy, scope, chainName, storeName, isActive, isDefault } = params;

  return useQuery({
    queryKey: [
      "tasks",
      "availability",
      subtype,
      { page, limit, search, sortBy, orderBy, scope, chainName, storeName, isActive, isDefault },
    ],
    queryFn: async () => {
      const res = await api.GET("/v1/tasks/{subtype}/availability", {
        params: {
          path: { subtype },
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[scope]": scope,
            "filter[chainName]": chainName || undefined,
            "filter[storeName]": storeName || undefined,
            "filter[isActive]": isActive,
            "filter[isDefault]": isDefault,
          },
        },
      });
      if (res.error) throw new Error("Failed to load task availability");
      return res.data as unknown as components["schemas"]["TasksAvailabilityListResponse"];
    },
    enabled: enabled && !!subtype,
  });
}

export function useTaskCardQuery(subtype: string, scope: TaskCardScope, enabled = true) {
  return useQuery({
    queryKey: ["tasks", "card", subtype, scope.chainName ?? null, scope.storeName ?? null],
    queryFn: async () => {
      const res = await api.GET("/v1/tasks/{subtype}/card", {
        params: {
          path: { subtype },
          query: { chainName: scope.chainName || undefined, storeName: scope.storeName || undefined },
        },
      });
      if (res.error) throw new Error("Failed to load task card");
      return res.data as unknown as TaskCard;
    },
    enabled: enabled && !!subtype,
  });
}

export function useSaveTaskCardMutation(subtype: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { scope: TaskCardScope; settings: TaskCardSettings }) => {
      const { locationStatus, ...rest } = params.settings;
      const res = await api.PATCH("/v1/tasks/{subtype}/card", {
        params: {
          path: { subtype },
          query: {
            chainName: params.scope.chainName || undefined,
            storeName: params.scope.storeName || undefined,
          },
        },
        // locationStatus is nested on the way out and flat on the way in — the override table
        // stores the two colours as separate columns.
        body: {
          ...rest,
          locationStatusColor: locationStatus?.color ?? null,
          locationStatusFontColor: locationStatus?.textColor ?? null,
        } as unknown as components["schemas"]["TasksCardEditRequest"],
      });
      if (res.error) throw new Error("Failed to save task card");
      return res.data as unknown as components["schemas"]["StatusResponse"];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useDeleteTaskCardOverrideMutation(subtype: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (scope: TaskCardScope) => {
      const res = await api.DELETE("/v1/tasks/{subtype}/card", {
        params: {
          path: { subtype },
          query: { chainName: scope.chainName || undefined, storeName: scope.storeName || undefined },
        },
      });
      if (res.error) throw new Error("Failed to remove task card override");
      return res.data as unknown as components["schemas"]["StatusResponse"];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useToggleTaskActiveMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { subtype: string; isActive: boolean }) => {
      const res = await api.PATCH("/v1/tasks/{subtype}/active", {
        params: { path: { subtype: params.subtype } },
        body: { isActive: params.isActive },
      });
      if (res.error) throw new Error("Failed to update custom task status");
      return res.data as unknown as components["schemas"]["StatusResponse"];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

/**
 * An availability row as the dialog submits it. `rules` is free-form JSON, which the generated
 * types widen to `Record<string, never>[]`, so the cast lives here rather than at every call site.
 */
export interface TaskAvailabilityInput {
  chainName?: string;
  storeName?: string;
  rules: Record<string, unknown>[] | null;
  isActive: boolean;
  isDefault: boolean;
  priority: number;
}

export function useCreateTaskAvailabilityMutation(subtype: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TaskAvailabilityInput) => {
      const res = await api.POST("/v1/tasks/{subtype}/availability", {
        params: { path: { subtype } },
        body: input as unknown as components["schemas"]["TasksAvailabilityCreateRequest"],
      });
      if (res.error) throw new Error("Failed to add availability");
      return res.data as unknown as components["schemas"]["TasksAvailabilityCreateResponse"];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useUpdateTaskAvailabilityMutation(subtype: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      availabilityId: number;
      input: Omit<TaskAvailabilityInput, "chainName" | "storeName">;
    }) => {
      const res = await api.PATCH("/v1/tasks/{subtype}/availability/{availability_id}", {
        params: { path: { subtype, availability_id: params.availabilityId } },
        body: params.input as unknown as components["schemas"]["TasksAvailabilityEditRequest"],
      });
      if (res.error) throw new Error("Failed to update availability");
      return res.data as unknown as components["schemas"]["StatusResponse"];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useDeleteTaskAvailabilityMutation(subtype: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (availabilityId: number) => {
      const res = await api.DELETE("/v1/tasks/{subtype}/availability/{availability_id}", {
        params: { path: { subtype, availability_id: availabilityId } },
      });
      if (res.error) throw new Error("Failed to remove availability");
      return res.data as unknown as components["schemas"]["StatusResponse"];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}
